import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const SHORT_CLOSE_NUMBER_PATTERN = /^SG-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

export const PurchaseOrderShortCloseNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^SG-[0-9]{8}-[0-9A-F]{12}$",
});
export type PurchaseOrderShortCloseNumber = Static<typeof PurchaseOrderShortCloseNumberSchema>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function hasSufficientSourceEntropy(value: string): boolean {
  return (
    SOURCE_PATTERN.test(value) &&
    [...value].filter((character) => /[A-Za-z0-9]/u.test(character)).length >= 12
  );
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function isPurchaseOrderShortCloseNumber(
  value: unknown,
): value is PurchaseOrderShortCloseNumber {
  if (!Value.Check(PurchaseOrderShortCloseNumberSchema, value)) return false;
  const match = SHORT_CLOSE_NUMBER_PATTERN.exec(value);
  if (!match) return false;
  return isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`);
}

export function derivePurchaseOrderShortCloseNumber(
  businessDate: string,
  shortCloseId: string,
): PurchaseOrderShortCloseNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!hasSufficientSourceEntropy(shortCloseId)) {
    throw new TypeError("shortCloseId must contain at least 12 safe alphanumeric characters");
  }
  return `SG-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(`${businessDate}\0${shortCloseId}`)}`;
}
